// frontend/src/pages/builder/Wells.jsx
// What is in the chart, as three slots: X axis, Values, Group by.
// 6c-1: each chip is removable. 6c-2: each slot is a drop target for fields
// dragged from the field list, and chips can be dragged from one slot to
// another; a value chip has an aggregation picker and a date chip a grain
// picker. 7c-2: restyled to the graphite look (slots stacked in the Visual
// panel, chips as pills). Clicking fields in the list still works, so the
// builder never depends on drag and drop (keyboard and touch users).
import React, { useState } from 'react';
import { ChevronDown, GripVertical, X } from 'lucide-react';
import { PanelLabel } from '@/components/ui/panel.jsx';
import { AGGREGATION_LABELS, GRAINS, aggregationsFor, wellsOf } from './builderSpec';

export const FIELD_MIME = 'application/x-connex-field';
export const CHIP_MIME = 'application/x-connex-chip';

/** A native select dressed as part of the chip: no box, muted text, a small chevron. */
export function ChipPicker({ label, value, options, onChange }) {
  return (
    <span className="relative inline-flex items-center">
      <select
        aria-label={label} value={value} onChange={(e) => onChange(e.target.value)}
        className="h-6 appearance-none rounded-md bg-transparent pl-1 pr-4 text-[11.5px] text-[var(--ui-text-muted)] hover:text-[var(--ui-text)] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
      >
        {options.map(([v, text]) => <option key={v} value={v}>{text}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-0.5 h-3 w-3 text-[var(--ui-text-muted)]" aria-hidden="true" />
    </span>
  );
}

function Chip({ chip, field, onRemove, onAggregation, onGrain }) {
  const m = chip.kind === 'measure' ? chip.item : null;
  const d = chip.kind === 'dimension' ? chip.item : null;
  const aggs = m && m.field ? aggregationsFor(field) : [];
  const name = field ? field.display_name : (m ? m.field : d.field);
  return (
    <span
      draggable
      onDragStart={(e) => {
        e.dataTransfer.setData(CHIP_MIME, JSON.stringify({ kind: chip.kind, index: chip.index }));
        e.dataTransfer.effectAllowed = 'move';
      }}
      title="Drag to another slot"
      className="inline-flex h-7 max-w-full cursor-grab items-center gap-1 rounded-full bg-[var(--ui-chip)] pl-1.5 pr-1 text-[12.5px] text-[var(--ui-text)] active:cursor-grabbing"
    >
      {/* The grip and the name are the places to grab; a dropdown can't start a drag. */}
      <GripVertical className="h-3 w-3 shrink-0 text-[var(--ui-text-muted)]" aria-hidden="true" />
      {m && aggs.length > 1 ? (
        <>
          <ChipPicker label={`How to combine ${name}`} value={m.aggregation}
            options={aggs.map((a) => [a, AGGREGATION_LABELS[a]])} onChange={(v) => onAggregation(chip.index, v)} />
          <span className="truncate">{name}</span>
        </>
      ) : d && d.grain ? (
        <>
          <span className="truncate">{name}</span>
          <ChipPicker label={`Group ${name} by`} value={d.grain}
            options={GRAINS.map((g) => [g, g])} onChange={(v) => onGrain(chip.index, v)} />
        </>
      ) : (
        <span className="truncate">{chip.label}</span>
      )}
      <button
        type="button" onClick={() => onRemove(chip.kind, chip.index)} aria-label={`Remove ${chip.label}`}
        className="rounded-full p-0.5 text-[var(--ui-text-muted)] hover:bg-white/10 hover:text-[var(--ui-text)]"
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    </span>
  );
}

export function Well({ id, label, chips, placeholder, fieldsByKey, handlers }) {
  const [over, setOver] = useState(false);
  const types = (e) => Array.from(e.dataTransfer.types || []);
  const empty = chips.length === 0;
  return (
    <div>
      <PanelLabel id={`well-${id}-label`}>{label}</PanelLabel>
      <div
        role="group" aria-labelledby={`well-${id}-label`}
        onDragOver={(e) => {
          const t = types(e);
          if (t.includes(FIELD_MIME) || t.includes(CHIP_MIME)) {
            e.preventDefault();
            e.dataTransfer.dropEffect = t.includes(CHIP_MIME) ? 'move' : 'copy';
            setOver(true);
          }
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          setOver(false);
          const key = e.dataTransfer.getData(FIELD_MIME);
          if (key) { e.preventDefault(); handlers.onDrop(key, id); return; }
          const raw = e.dataTransfer.getData(CHIP_MIME);
          if (!raw) return;
          e.preventDefault();
          try {
            const chip = JSON.parse(raw);
            if ((chip.kind === 'dimension' || chip.kind === 'measure') && Number.isInteger(chip.index)) {
              handlers.onMoveChip(chip.kind, chip.index, id);
            }
          } catch (err) { /* not one of our chips: ignore */ }
        }}
        className={`flex min-h-[42px] flex-wrap items-center gap-1.5 rounded-xl p-2 transition-[background-color,box-shadow] ${
          over ? 'bg-[var(--ui-inset-hover)] shadow-[inset_0_0_0_1.5px_var(--ui-selected)]'
            : empty ? 'border border-dashed border-[var(--ui-dash)] pl-3'
              : 'bg-[var(--ui-inset)]'}`}
      >
        {empty
          ? <span className="text-[12.5px] text-[var(--ui-text-muted)]">{placeholder}</span>
          : chips.map((c) => (
            <Chip key={`${c.kind}-${c.index}`} chip={c} field={fieldsByKey[c.item.field]}
              onRemove={handlers.onRemove} onAggregation={handlers.onAggregation} onGrain={handlers.onGrain} />
          ))}
      </div>
    </div>
  );
}

export default function Wells({ draft, fieldsByKey, onRemove, onDrop, onMoveChip, onAggregation, onGrain }) {
  const w = wellsOf(draft, fieldsByKey);
  const handlers = { onRemove, onDrop, onMoveChip, onAggregation, onGrain };
  return (
    <div className="flex flex-col">
      <Well id="x" label="X axis" chips={w.x} placeholder="Drop or click a field" fieldsByKey={fieldsByKey} handlers={handlers} />
      <Well id="values" label="Values" chips={w.values} placeholder="Count of records" fieldsByKey={fieldsByKey} handlers={handlers} />
      <Well id="groupBy" label="Group by" chips={w.groupBy} placeholder="Optional · splits the chart by a field" fieldsByKey={fieldsByKey} handlers={handlers} />
    </div>
  );
}